import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import BrandChip from '../components/BrandChip';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { 
  CheckCircle, 
  ChevronDown, 
  ChevronUp, 
  Phone, 
  ArrowRight,
  TreePalm,
  Home,
  Building2,
  MapPin
} from 'lucide-react';

const RoboticLawnMowersPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const useCases = [
    {
      icon: Home,
      title: "Luxury Villas & Farmhouses",
      description: "Perfect for estates with large lawns. Autonomous mowing without the need for manual labor or noisy petrol mowers."
    },
    {
      icon: TreePalm,
      title: "Resorts & Hotels",
      description: "Keep your property's lawns pristine for guests. Silent operation won't disturb visitors."
    },
    {
      icon: Building2,
      title: "Corporate Campuses",
      description: "Large-scale lawn maintenance for IT parks, office complexes, and educational institutions."
    },
    {
      icon: MapPin,
      title: "Golf Courses",
      description: "Precision mowing for fairways and roughs. Consistent cut height across the entire course."
    }
  ];

  const faqs = [
    {
      question: "How do robotic lawn mowers navigate large areas?",
      answer: "Harpalyk's Greenova uses RTK (Real-Time Kinematic) GPS combined with AI vision for centimeter-level accuracy. Unlike basic models that need boundary wires, our robots create a virtual map of your lawn and navigate autonomously. They can handle complex layouts, multiple zones, and obstacles."
    },
    {
      question: "Can robotic mowers handle slopes and uneven terrain?",
      answer: "Yes, the Harpalyk Greenova can handle slopes up to 55% (approximately 29 degrees). It's equipped with all-terrain wheels and intelligent traction control. For steep areas, the robot adjusts its cutting pattern to ensure even coverage and prevent slipping."
    },
    {
      question: "How large an area can a robotic lawn mower cover?",
      answer: "The Greenova 5.0 can cover up to 5000 square meters (approximately 1.25 acres) on a single charge. For larger properties, multiple robots can be coordinated, or the robot will automatically return to charge and resume mowing until the entire area is complete."
    },
    {
      question: "What maintenance does a robotic lawn mower need?",
      answer: "Minimal maintenance is required: clean the blades monthly, check the wheels for debris, and ensure the charging station contacts are clean. The blades should be replaced every 3-6 months depending on usage. Harpalyk provides a 2-year warranty and annual maintenance service."
    },
    {
      question: "Are robotic lawn mowers safe around children and pets?",
      answer: "Yes, Harpalyk robots include multiple safety features: lift sensors that stop blades instantly if the robot is lifted, collision detection to avoid objects, and ultrasonic sensors to detect approaching objects. The quiet operation and small blade size also make them inherently safer than traditional mowers."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>LAWNBOT — Intelligent Robotic Lawn Mowers | Harpalyk India</title>
        <meta name="description" content="Autonomous robotic lawn mowers with RTK + AI vision — no boundary wires, 55% slope capability. For villas, farmhouses & resorts across India. Free site survey. ₹2,69,990." />
        <meta name="keywords" content="robotic lawn mower india, automatic grass cutter, robot lawn mower, autonomous lawn mower, lawn robot india, commercial lawn mower robot" />
        <link rel="canonical" href="https://harpalyk.com/robotic-lawn-mowers" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-20">
        <div className="h-1 bg-gradient-to-r from-green-500 via-green-400 to-transparent" data-testid="family-accent-bar-lawnbot" aria-hidden="true" />
        <Breadcrumbs items={[{ label: 'Robotic Lawn Mowers' }]} />
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 text-white py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-green-500/20 text-green-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  HARPALYK · LAWNBOT FAMILY
                </span>
                <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                  LAWNBOT — Intelligent Robotic Lawn Mowers
                </h1>
                <p className="text-xl text-green-100 mb-8 leading-relaxed">
                  Autonomous <strong>robotic lawn mowers</strong> with centimeter-level precision. No boundary wires needed. 
                  Perfect for large estates, farmhouses, golf courses, and hospitality properties across India.
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <a href="#products" className="bg-green-500 hover:bg-green-400 text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all">
                    View Lawn Robots <ArrowRight className="w-5 h-5" />
                  </a>
                  <a href="tel:+917011370322" className="border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-lg font-semibold flex items-center gap-2 transition-all">
                    <Phone className="w-5 h-5" /> Free Site Survey
                  </a>
                </div>
                
                <div className="flex flex-wrap gap-6 pt-8 border-t border-white/20">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-400">55%</p>
                    <p className="text-sm text-green-200">Max Slope</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-400">5000m²</p>
                    <p className="text-sm text-green-200">Coverage Area</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-400">RTK GPS</p>
                    <p className="text-sm text-green-200">Precision</p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1558904541-efa843a96f01?w=600&q=80" 
                  alt="Robotic Lawn Mower India" 
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-4 -right-4 bg-white text-green-900 px-6 py-3 rounded-xl shadow-lg">
                  <p className="font-bold">₹2,69,990</p>
                  <p className="text-sm text-gray-600">Installation included</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technology */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-green-600 font-semibold uppercase tracking-wide text-sm">Technology</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                RTK + AI Vision: No Boundary Wires Needed
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Unlike traditional robot mowers that require buried boundary wires, Harpalyk Greenova uses satellite positioning and AI vision to navigate your lawn with centimeter-level accuracy.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { 
                  title: "RTK GPS Navigation", 
                  desc: "Centimeter-level positioning using satellite technology. Creates a virtual boundary without physical wires.",
                  icon: "📡"
                },
                { 
                  title: "AI Vision System", 
                  desc: "Camera-based obstacle detection identifies trees, furniture, pets, and people in real-time.",
                  icon: "👁️"
                },
                { 
                  title: "Multi-Zone Support", 
                  desc: "Program different cutting heights and schedules for different areas of your lawn.",
                  icon: "🗺️"
                }
              ].map((item, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
                  <span className="text-4xl mb-4 block">{item.icon}</span>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-green-600 font-semibold uppercase tracking-wide text-sm">Applications</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Perfect for Large Properties
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="bg-green-100 p-4 rounded-xl">
                      <useCase.icon className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{useCase.title}</h3>
                      <p className="text-gray-600">{useCase.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product */}
        <section id="products" className="py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-green-600 font-semibold uppercase tracking-wide text-sm">Our Product</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Harpalyk Greenova 5.0
              </h2>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <div className="grid md:grid-cols-2">
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_smart-living-18/artifacts/h221a4vl_4885c0df-e93f-4fc7-b5f9-c51e42ee3ac0-md.jpeg" 
                    alt="Harpalyk LAWNBOT Greenova 5.0 — robotic lawn mower" 
                    className="w-full h-80 object-cover"
                  />
                  <BrandChip family="LAWNBOT" />
                </div>
                <div className="p-8">
                  <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">Premium Model</span>
                  <p className="text-[11px] font-semibold tracking-[0.18em] text-gray-400 uppercase mb-1">HARPALYK · LAWNBOT</p>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1 mb-2">Greenova 5.0</h3>
                  <p className="text-3xl font-bold text-green-600 mb-4">₹2,69,990</p>
                  
                  <ul className="space-y-3 mb-6">
                    {[
                      "RTK + AI Vision navigation",
                      "No boundary wires needed",
                      "5000m² coverage area",
                      "55% slope capability",
                      "Multi-zone programming",
                      "Weather-resistant (IPX5)",
                      "2-year warranty + installation"
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <a href="/products/greenova-5" className="block text-center bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-all">
                    View Greenova 5.0 — Specs & Price
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-green-600 to-emerald-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready for Effortless Lawn Maintenance?
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Schedule a free site survey. Our team will assess your property and recommend the best solution for your lawn care needs.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:+917011370322" className="bg-white text-green-700 px-8 py-4 rounded-lg font-bold hover:bg-green-100 transition-all flex items-center gap-2">
                <Phone className="w-5 h-5" /> +91-7011370322
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-green-600 font-semibold uppercase tracking-wide text-sm">FAQs</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">
                Frequently Asked Questions About Robotic Lawn Mowers
              </h2>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <button 
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {openFaq === index ? <ChevronUp className="w-5 h-5 text-green-600" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
};

export default RoboticLawnMowersPage;
